import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid-pro';
import { useQuery } from '@tanstack/react-query';
import { UserKeys, getFeatures } from '../../../../api/users-api';
import { useMemo, useState } from 'react';
import FeaturesGridToolbar from './features-grid-toolbar';
import AddFeatureDialog from './add-feature-dialog';
import { FeatureModel } from '../../../../../types';
import DeleteFeatureDialog from './delete-feature-dialog';
import RenameFeatureDialog from './rename-feature-dialog';
import {
  CalendarRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { useUserSettings } from '../../../../utils/use-user-settings';
import { Messages } from '../../../../utils/message-constants';
import { createGridOverlays } from '../../../grid-overlay/no-data-overlay-helpers';
import SharedDataGridClient from '../../../../shared/shared-data-grid-client';
import { useToolbar } from '../../../../shared/shared-data-grid-toolbar-helper';

import Tooltip from '@mui/material/Tooltip';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Star from '@mui/icons-material/Star';

export default function FeaturesGrid() {
  const { calendarRoles, changeLogRoles } = useUserSettings();

  const [selectedFeature, setSelectedFeature] = useState<FeatureModel | null>(
    null
  );
  const [openDeleteFeature, setOpenDeleteFeature] = useState(false);
  const [openCreateFeature, setOpenCreateFeature] = useState(false);
  const [openRenameFeature, setOpenRenameFeature] = useState(false);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [UserKeys.Features],
    queryFn: () => getFeatures(),
    meta: {
      errorMessage: Messages.Errors.Network.Loading('features'),
    },
  });

  const columns: GridColDef<FeatureModel>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Feature Name',
        flex: 1,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        getActions: (params) => {
          const buttonArray = [];
          if (
            isAbleToSeeUIElement(
              calendarRoles,
              CalendarRoles.EditFeatures.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="Rename"
                icon={
                  <Tooltip arrow title="Rename">
                    <Edit />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedFeature(params.row);
                  setOpenRenameFeature(true);
                }}
              />
            );
          }
          if (
            isAbleToSeeUIElement(
              calendarRoles,
              CalendarRoles.DeleteFeatures.value
            )
          ) {
            buttonArray.push(
              <GridActionsCellItem
                label="Delete"
                icon={
                  <Tooltip arrow title="Delete">
                    <Delete />
                  </Tooltip>
                }
                onClick={() => {
                  setSelectedFeature(params.row);
                  setOpenDeleteFeature(true);
                }}
              />
            );
          }
          return buttonArray;
        },
      },
    ],
    [calendarRoles]
  );

  const toolbarProps = useMemo(
    () => ({
      calendarRoles: calendarRoles,
      setOpenCreateFeature,
    }),
    [calendarRoles]
  );

  return (
    <>
      <SharedDataGridClient
        key="features-grid"
        slots={{
          toolbar: useToolbar(FeaturesGridToolbar, toolbarProps),
          ...createGridOverlays('Features', <Star />),
        }}
        disableRowSelectionOnClick
        getRowId={(row) => row.etags}
        rows={data || []}
        columns={columns}
        loading={isLoading || isFetching}
        sx={{
          '& .MuiDataGrid-row': {
            cursor: 'default',
          },
          '& .MuiDataGrid-toolbar .MuiTextField-root': {
            width: '400px',
          },
        }}
      />
      {openCreateFeature && (
        <AddFeatureDialog
          open={openCreateFeature}
          handleClose={() => setOpenCreateFeature(false)}
          changeLogRoles={changeLogRoles}
        />
      )}
      {selectedFeature && openDeleteFeature ? (
        <DeleteFeatureDialog
          open={openDeleteFeature}
          handleClose={() => setOpenDeleteFeature(false)}
          selectedFeature={selectedFeature}
          setSelectedFeature={setSelectedFeature}
          changeLogRoles={changeLogRoles}
        />
      ) : null}
      {selectedFeature && openRenameFeature ? (
        <RenameFeatureDialog
          open={openRenameFeature}
          handleClose={() => setOpenRenameFeature(false)}
          changeLogRoles={changeLogRoles}
          selectedFeature={selectedFeature}
          setSelectedFeature={setSelectedFeature}
        />
      ) : null}
    </>
  );
}
